import {
  HEALTH_CHECK_INTERVAL,
  INACTIVITY_THRESHOLD_IN_SECONDS,
  MAX_CRASH_COUNT,
  TIME_BEFORE_TIMEOUT,
  ERROR_TRACKING_CONFIG,
  ERROR_TYPE_LIMITS,
  ERROR_TYPE_THRESHOLDS,
  ERROR_TYPES,
  HEALTH_MONITOR_EVENTS,
  INACTIVITY_THRESHOLD,
} from "../common/constants/healthCheck.constants";
import {
  formatTimeRemaining,
  createTimestampedError,
  keepLastNItems,
} from "../common/utils/health-check.utils";

export interface AvatarHealthStatus {
  isHealthy: boolean;
  lastActivity: Date;
  crashCount: number;
  errorMessages: string[];
  errorTypes: Map<string, number>;
  lastErrorType: string | null;
  consecutiveErrorsOfSameType: number;
}

/**
 * Monitors avatar health, tracks errors, and manages reconnection logic
 */
class AvatarHealthMonitorService {
  private healthStatus: AvatarHealthStatus = {
    isHealthy: true,
    lastActivity: new Date(),
    crashCount: 0,
    errorMessages: [],
    errorTypes: new Map(),
    lastErrorType: null,
    consecutiveErrorsOfSameType: 0,
  };

  private healthCheckInterval: NodeJS.Timeout | null = null;
  private preTimeoutWarned: boolean = false;

  /**
   * Start monitoring avatar health
   */
  startMonitoring(): void {
    this.stopMonitoring();
    this.updateActivity();
    this.preTimeoutWarned = false;
    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, HEALTH_CHECK_INTERVAL);
  }

  /**
   * Stop monitoring avatar health
   */
  stopMonitoring(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
    this.preTimeoutWarned = false;
  }

  /**
   * Update last activity timestamp
   */
  updateActivity(): void {
    this.healthStatus.lastActivity = new Date();
    this.healthStatus.isHealthy = true;
    this.preTimeoutWarned = false;
    const activityEvent = new CustomEvent(
      HEALTH_MONITOR_EVENTS.AVATAR_ACTIVITY
    );
    window.dispatchEvent(activityEvent);
  }

  /**
   * Record avatar crash and track error patterns
   */
  recordCrash(error: string, errorType?: string): void {
    this.healthStatus.crashCount++;
    this.healthStatus.errorMessages.push(createTimestampedError(error));
    this.healthStatus.isHealthy = false;

    if (errorType) {
      const currentCount = this.healthStatus.errorTypes.get(errorType) || 0;
      this.healthStatus.errorTypes.set(errorType, currentCount + 1);

      if (this.healthStatus.lastErrorType === errorType) {
        this.healthStatus.consecutiveErrorsOfSameType++;
      } else {
        this.healthStatus.consecutiveErrorsOfSameType = 1;
        this.healthStatus.lastErrorType = errorType;
      }
    }

    if (
      this.healthStatus.errorMessages.length >
      ERROR_TRACKING_CONFIG.MAX_ERROR_MESSAGES
    ) {
      this.healthStatus.errorMessages = keepLastNItems(
        this.healthStatus.errorMessages,
        ERROR_TRACKING_CONFIG.MAX_ERROR_MESSAGES
      );
    }

    const crashEvent = new CustomEvent(
      HEALTH_MONITOR_EVENTS.AVATAR_HEALTH_CRASH,
      {
        detail: {
          crashCount: this.healthStatus.crashCount,
          error,
          errorType: errorType || ERROR_TYPES.UNKNOWN,
          consecutiveErrorsOfSameType:
            this.healthStatus.consecutiveErrorsOfSameType,
          shouldRestart: this.shouldAllowRestart(errorType),
          healthStatus: this.getHealthStatus(),
        },
      }
    );
    window.dispatchEvent(crashEvent);
  }

  /**
   * Perform periodic health check
   */
  private performHealthCheck(): void {
    const now = new Date();
    const timeSinceLastActivity =
      now.getTime() - this.healthStatus.lastActivity.getTime();

    const inactivityThreshold = INACTIVITY_THRESHOLD;
    const remainingMsBeforeTimeout =
      inactivityThreshold - timeSinceLastActivity;

    if (
      !this.preTimeoutWarned &&
      remainingMsBeforeTimeout <= TIME_BEFORE_TIMEOUT &&
      remainingMsBeforeTimeout > 0
    ) {
      try {
        const warnEvent = new CustomEvent(
          HEALTH_MONITOR_EVENTS.AVATAR_IDLE_WARNING,
          {
            detail: {
              secondsRemaining: TIME_BEFORE_TIMEOUT / 1000,
            },
          }
        );
        window.dispatchEvent(warnEvent);
        this.preTimeoutWarned = true;
      } catch (error) {}
    }

    if (timeSinceLastActivity >= inactivityThreshold) {
      const timeLeft = formatTimeRemaining(INACTIVITY_THRESHOLD_IN_SECONDS);
      const crashEvent = new CustomEvent(HEALTH_MONITOR_EVENTS.AVATAR_ERROR, {
        detail: {
          error: `Avatar unresponsive - no activity for ${timeLeft}`,
          type: ERROR_TYPES.TIMEOUT,
        },
      });
      window.dispatchEvent(crashEvent);

      this.recordCrash("Avatar unresponsive - no activity detected");
      this.stopMonitoring();
    }
  }

  /**
   * Reset crash count and error tracking
   */
  resetCrashCount(): void {
    this.healthStatus.crashCount = 0;
    this.healthStatus.errorMessages = [];
    this.healthStatus.errorTypes.clear();
    this.healthStatus.lastErrorType = null;
    this.healthStatus.consecutiveErrorsOfSameType = 0;
  }

  /**
   * Determine if restart should be allowed based on error type and patterns
   */
  private shouldAllowRestart(errorType?: string): boolean {
    if (this.healthStatus.crashCount >= MAX_CRASH_COUNT) {
      return false;
    }

    if (
      this.healthStatus.consecutiveErrorsOfSameType >=
      ERROR_TRACKING_CONFIG.CONSECUTIVE_ERROR_THRESHOLD
    ) {
      return false;
    }

    if (errorType) {
      switch (errorType) {
        case ERROR_TYPES.BROWSER:
          return this.healthStatus.crashCount < ERROR_TYPE_LIMITS.BROWSER;
        case ERROR_TYPES.AVATAR_SERVICE:
          return (
            this.healthStatus.crashCount < ERROR_TYPE_LIMITS.AVATAR_SERVICE
          );
        case ERROR_TYPES.NETWORK:
          return this.healthStatus.crashCount < ERROR_TYPE_LIMITS.NETWORK;
        default:
          return this.healthStatus.crashCount < ERROR_TYPE_LIMITS.DEFAULT;
      }
    }

    return this.healthStatus.crashCount < MAX_CRASH_COUNT;
  }

  /**
   * Get current health status
   */
  getHealthStatus(): AvatarHealthStatus {
    return { ...this.healthStatus };
  }

  /**
   * Check if avatar is healthy
   */
  isAvatarHealthy(): boolean {
    if (
      !this.healthStatus.isHealthy ||
      this.healthStatus.crashCount >= MAX_CRASH_COUNT
    ) {
      return false;
    }

    if (
      this.healthStatus.consecutiveErrorsOfSameType >=
      ERROR_TRACKING_CONFIG.CONSECUTIVE_ERROR_THRESHOLD
    ) {
      return false;
    }

    const networkErrors =
      this.healthStatus.errorTypes.get(ERROR_TYPES.NETWORK) || 0;
    const avatarServiceErrors =
      this.healthStatus.errorTypes.get(ERROR_TYPES.AVATAR_SERVICE) || 0;

    if (networkErrors >= ERROR_TYPE_THRESHOLDS.NETWORK_ERRORS) {
      return false;
    }

    if (avatarServiceErrors >= ERROR_TYPE_THRESHOLDS.AVATAR_SERVICE_ERRORS) {
      return false;
    }

    return true;
  }
}

export const avatarHealthMonitor = new AvatarHealthMonitorService();
